import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, Plus, Wrench, Trash2, X, ChevronDown, ChevronUp, Check, ExternalLink, Zap } from 'lucide-react'
import api from '../services/api'
import CablesHealth from '../components/CablesHealth'

// ─── Types ───────────────────────────────────────────────────────────────────

interface MCPConnector {
  id: string
  name: string
  url: string
  icon: string
  description: string
  type: string
  api_token?: string
  enabled?: number
  status?: 'connected' | 'disconnected' | 'checking'
}

interface KiroProvider {
  id: string
  name: string
  icon: string
  color: string
  models: string[]
  api_url: string
  connected: boolean | null
  enabled_mcp_count: number
  permissions: Record<string, boolean>
}

interface ProviderPermission {
  connector_id: string
  connector_name: string
  connector_icon: string
  connector_type: string
  connector_active: boolean
  enabled: boolean
}

interface MCPTool {
  name: string
  description: string
  inputSchema?: any
}

interface NewConnectorForm {
  name: string; url: string; icon: string; description: string; type: string; api_token: string
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TYPE_META: Record<string, { label: string; bg: string; color: string }> = {
  cms:        { label: 'CMS',        bg: '#1e3a5f', color: '#60a5fa' },
  database:   { label: 'Database',   bg: '#14291a', color: '#4ade80' },
  proxy:      { label: 'Proxy',      bg: '#2d1b4e', color: '#a78bfa' },
  http:       { label: 'HTTP',       bg: '#1a2535', color: '#94a3b8' },
  'sub-hub':  { label: 'Hub MCP',    bg: '#1a2e1a', color: '#86efac' },
  'claude-ai':{ label: 'Claude.ai',  bg: '#2d1b4e', color: '#c4b5fd' },
  webui:      { label: 'Web UI',     bg: '#1c1a2e', color: '#f9a8d4' },
  external:   { label: 'API Ext.',   bg: '#1e293b', color: '#64748b' },
}

const EMPTY_FORM: NewConnectorForm = { name: '', url: '', icon: '🔌', description: '', type: 'external', api_token: '' }

// ─── Small reusable components ────────────────────────────────────────────────

function StatusDot({ status, size = 10 }: { status?: string; size?: number }) {
  const color = status === 'connected' ? '#22c55e' : status === 'disconnected' ? '#ef4444' : '#f59e0b'
  return (
    <span style={{
      display: 'inline-block', width: `${size}px`, height: `${size}px`, borderRadius: '50%',
      backgroundColor: color, boxShadow: `0 0 6px ${color}`, flexShrink: 0,
      animation: (!status || status === 'checking') ? 'pulse 1.5s ease-in-out infinite' : 'none',
    }} />
  )
}

function TypeBadge({ type }: { type: string }) {
  const m = TYPE_META[type] || TYPE_META.external
  return (
    <span style={{ background: m.bg, color: m.color, padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  )
}

function MiniToggle({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <div
      onClick={() => onChange(!enabled)}
      style={{
        width: '36px', height: '20px', borderRadius: '10px', cursor: 'pointer', flexShrink: 0,
        backgroundColor: enabled ? '#22c55e' : '#374151', position: 'relative', transition: 'background 0.2s',
      }}
    >
      <div style={{
        position: 'absolute', top: '2px', left: enabled ? '18px' : '2px', width: '16px', height: '16px',
        borderRadius: '50%', backgroundColor: 'white', transition: 'left 0.2s',
      }} />
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ConnecteursPage() {
  const [connectors, setConnectors] = useState<MCPConnector[]>([])
  const [providers, setProviders] = useState<KiroProvider[]>([])
  const [selectedProvider, setSelectedProvider] = useState<KiroProvider | null>(null)
  const [permissions, setPermissions] = useState<ProviderPermission[]>([])
  const [loadingPerms, setLoadingPerms] = useState(false)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // Tools modal
  const [viewingTools, setViewingTools] = useState<MCPConnector | null>(null)
  const [tools, setTools] = useState<MCPTool[]>([])
  const [loadingTools, setLoadingTools] = useState(false)
  const [toolSearch, setToolSearch] = useState('')
  const [expandedTool, setExpandedTool] = useState<string | null>(null)

  // Add form
  const [showAddForm, setShowAddForm] = useState(false)
  const [newForm, setNewForm] = useState<NewConnectorForm>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)

  const [activeTab, setActiveTab] = useState<'matrix' | 'connectors'>('matrix')

  const loadData = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true)
    else setRefreshing(true)
    try {
      const [connRes, provRes] = await Promise.all([
        fetch('/api/mcp-connectors'),
        fetch('/api/kiro-providers'),
      ])
      if (connRes.ok) setConnectors(await connRes.json())
      if (provRes.ok) {
        const prov: KiroProvider[] = await provRes.json()
        setProviders(prov)
        if (!selectedProvider && prov.length > 0) setSelectedProvider(prov[0])
      }
    } catch (e) { console.error(e) }
    setLoading(false)
    setRefreshing(false)
  }, [])

  const loadPermissions = useCallback(async (providerId: string) => {
    setLoadingPerms(true)
    try {
      const res = await fetch(`/api/mcp-permissions/${providerId}`)
      if (res.ok) setPermissions(await res.json())
    } catch (e) { console.error(e) }
    setLoadingPerms(false)
  }, [])

  useEffect(() => { loadData() }, [loadData])

  useEffect(() => {
    if (selectedProvider) loadPermissions(selectedProvider.id)
  }, [selectedProvider, loadPermissions])

  const handleSelectProvider = (p: KiroProvider) => {
    setSelectedProvider(p)
  }

  const handleTogglePerm = async (connectorId: string, enabled: boolean) => {
    if (!selectedProvider) return
    // Optimistic update
    setPermissions(prev => prev.map(p => p.connector_id === connectorId ? { ...p, enabled } : p))
    try {
      await fetch(`/api/mcp-permissions/${selectedProvider.id}/${connectorId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      })
    } catch (e) { console.error(e); loadPermissions(selectedProvider.id) }
  }

  const handleToggleAll = async (enabled: boolean) => {
    if (!selectedProvider) return
    setPermissions(prev => prev.map(p => ({ ...p, enabled })))
    try {
      await fetch(`/api/mcp-permissions/${selectedProvider.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      })
    } catch (e) { console.error(e) }
  }

  const handleToggleConnector = async (connector: MCPConnector) => {
    await fetch(`/api/mcp-connectors/${connector.id}/toggle`, { method: 'PATCH' })
    loadData(true)
  }

  const handleDeleteConnector = async (connector: MCPConnector) => {
    if (!confirm(`Supprimer « ${connector.name} » ?`)) return
    await fetch(`/api/mcp-connectors/${connector.id}`, { method: 'DELETE' })
    loadData(true)
  }

  const handleViewTools = async (connector: MCPConnector) => {
    setViewingTools(connector)
    setTools([]); setToolSearch(''); setExpandedTool(null); setLoadingTools(true)
    try {
      const res = await fetch(`/api/mcp-connectors/${connector.id}/tools`)
      if (res.ok) { const d = await res.json(); setTools(d.tools || []) }
    } catch (e) { console.error(e) }
    setLoadingTools(false)
  }

  const handleAddConnector = async () => {
    if (!newForm.name.trim() || !newForm.url.trim()) return
    setSaving(true)
    try {
      await api.post('/api/mcp-connectors', { name: newForm.name, url: newForm.url, icon: newForm.icon || '🔌', description: newForm.description, type: newForm.type, api_token: newForm.api_token || null })
      setShowAddForm(false); setNewForm(EMPTY_FORM); loadData(true)
    } catch (e) { console.error(e) }
    setSaving(false)
  }

  const enabledPerms = permissions.filter(p => p.enabled).length
  const connectedCount = connectors.filter(c => c.status === 'connected').length

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#6b7280' }}>
      <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite', marginRight: '10px' }} /> Chargement…
    </div>
  )

  return (
    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', backgroundColor: '#0f172a' }}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .perm-row:hover { background: #1e293b !important; }
        .provider-item:hover { background: #1e293b !important; }
        .connector-row:hover { background: #1e293b !important; }
      `}</style>

      {/* ── Top bar ── */}
      <div style={{ padding: '20px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div>
            <h1 style={{ color: '#f3f4f6', fontSize: '22px', fontWeight: '700', margin: 0 }}>🔌 Connecteurs & Permissions</h1>
            <p style={{ color: '#4b5563', fontSize: '12px', margin: '3px 0 0' }}>
              {connectedCount}/{connectors.length} MCP actifs · {providers.length} providers Kiro
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => loadData(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px', border: '1px solid #374151', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '12px' }}>
              <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} /> Rafraîchir
            </button>
            <button onClick={() => setShowAddForm(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', border: 'none', background: 'linear-gradient(to right, #ec4899, #a855f7)', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
              <Plus size={13} /> Ajouter MCP
            </button>
          </div>
        </div>

        {/* 📡 État des câbles (santé des liens inter-machines) */}
        <CablesHealth />

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '2px', borderBottom: '1px solid #1e293b' }}>
          {([['matrix', '🗺️ Matrice Permissions'], ['connectors', '🔌 Connecteurs']] as const).map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px',
              color: activeTab === tab ? '#ec4899' : '#6b7280', fontWeight: activeTab === tab ? '700' : '400',
              borderBottom: activeTab === tab ? '2px solid #ec4899' : '2px solid transparent',
              marginBottom: '-1px', transition: 'color 0.15s',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

        {activeTab === 'matrix' ? (
          <MatrixView
            providers={providers}
            selectedProvider={selectedProvider}
            permissions={permissions}
            loadingPerms={loadingPerms}
            connectors={connectors}
            enabledPerms={enabledPerms}
            onSelectProvider={handleSelectProvider}
            onTogglePerm={handleTogglePerm}
            onToggleAll={handleToggleAll}
            onViewTools={handleViewTools}
          />
        ) : (
          <ConnectorsView
            connectors={connectors}
            onToggle={handleToggleConnector}
            onDelete={handleDeleteConnector}
            onViewTools={handleViewTools}
          />
        )}
      </div>

      {/* Modals */}
      {viewingTools && (
        <ToolsModal
          connector={viewingTools} tools={tools.filter(t => !toolSearch || t.name.toLowerCase().includes(toolSearch.toLowerCase()) || (t.description||'').toLowerCase().includes(toolSearch.toLowerCase()))}
          allTools={tools} loading={loadingTools} search={toolSearch} onSearch={setToolSearch}
          expandedTool={expandedTool} onToggleExpand={n => setExpandedTool(expandedTool === n ? null : n)}
          onClose={() => { setViewingTools(null); setTools([]) }}
        />
      )}
      {showAddForm && (
        <AddModal form={newForm} onChange={setNewForm} onSave={handleAddConnector} onClose={() => { setShowAddForm(false); setNewForm(EMPTY_FORM) }} saving={saving} />
      )}
    </div>
  )
}

// ─── MatrixView ───────────────────────────────────────────────────────────────

function MatrixView({ providers, selectedProvider, permissions, loadingPerms, connectors, enabledPerms, onSelectProvider, onTogglePerm, onToggleAll, onViewTools }: {
  providers: KiroProvider[]; selectedProvider: KiroProvider | null; permissions: ProviderPermission[]
  loadingPerms: boolean; connectors: MCPConnector[]; enabledPerms: number
  onSelectProvider: (p: KiroProvider) => void; onTogglePerm: (id: string, v: boolean) => void
  onToggleAll: (v: boolean) => void; onViewTools: (c: MCPConnector) => void
}) {
  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

      {/* Left — Providers list */}
      <div style={{ width: '260px', minWidth: '260px', borderRight: '1px solid #1e293b', overflowY: 'auto', padding: '12px 8px' }}>
        <div style={{ fontSize: '10px', color: '#4b5563', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 8px 10px' }}>
          Providers Kiro
        </div>
        {providers.map(p => {
          const isSelected = selectedProvider?.id === p.id
          const pPerms = permissions
          const enabledCount = isSelected
            ? pPerms.filter(x => x.enabled).length
            : (Object.values(p.permissions).filter(Boolean).length)
          const totalConns = isSelected ? pPerms.length : Object.keys(p.permissions).length || connectors.length
          const isOllamaCloud = p.id === 'ollama-cloud'

          return (
            <div key={p.id} style={{ marginBottom: '2px' }}>
              <button
                onClick={() => onSelectProvider(p)}
                className="provider-item"
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '9px 12px', borderRadius: isSelected ? '8px 8px 0 0' : '8px', border: '1px solid',
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                  backgroundColor: isSelected ? `${p.color}18` : 'transparent',
                  borderColor: isSelected ? p.color + '66' : 'transparent',
                  borderBottom: isSelected ? 'none' : undefined,
                }}
              >
                <span style={{ fontSize: '20px', flexShrink: 0 }}>{p.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: isSelected ? '#f3f4f6' : '#d1d5db', fontSize: '13px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.name}
                    {isOllamaCloud && <span style={{ marginLeft: '6px', fontSize: '9px', background: '#14532d', color: '#4ade80', padding: '1px 5px', borderRadius: '6px', fontWeight: '700' }}>MCP ✓</span>}
                  </div>
                  <div style={{ fontSize: '10px', color: '#4b5563', marginTop: '1px' }}>
                    {p.models.length} modèles · {totalConns > 0 ? `${enabledCount}/${totalConns} MCP` : 'config'}
                  </div>
                </div>
                {enabledCount > 0 && (
                  <span style={{ fontSize: '10px', background: p.color + '33', color: p.color, padding: '1px 6px', borderRadius: '8px', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {enabledCount} ✓
                  </span>
                )}
              </button>
              {/* Action buttons row */}
              {isSelected && (
                <div style={{
                  display: 'flex', gap: '4px', padding: '6px 10px 8px',
                  backgroundColor: `${p.color}10`, border: `1px solid ${p.color}44`,
                  borderTop: 'none', borderRadius: '0 0 8px 8px',
                }}>
                  <ProviderActionBtn color={p.color} icon={<Check size={10} />} label="Tout ON" onClick={() => onToggleAll(true)} />
                  <ProviderActionBtn color="#ef4444" icon={<X size={10} />} label="Tout OFF" onClick={() => onToggleAll(false)} />
                  <a href={p.api_url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                    <ProviderActionBtn color="#64748b" icon={<ExternalLink size={10} />} label="Site" onClick={() => {}} />
                  </a>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: '10px', color: '#374151', alignSelf: 'center' }}>
                    {p.models.slice(0, 2).join(', ')}{p.models.length > 2 ? ` +${p.models.length - 2}` : ''}
                  </span>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Right — Permissions panel */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        {!selectedProvider ? (
          <div style={{ textAlign: 'center', color: '#4b5563', paddingTop: '60px' }}>
            Sélectionnez un provider
          </div>
        ) : (
          <>
            {/* Provider header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span style={{ fontSize: '32px' }}>{selectedProvider.icon}</span>
              <div style={{ flex: 1 }}>
                <h2 style={{ color: '#f3f4f6', margin: 0, fontSize: '18px', fontWeight: '700' }}>{selectedProvider.name}</h2>
                <p style={{ color: '#6b7280', margin: '2px 0 0', fontSize: '12px' }}>
                  {enabledPerms}/{permissions.length} outils MCP autorisés · {selectedProvider.models.length} modèles
                  {(selectedProvider.id === 'litellm' || selectedProvider.id === 'ollama-cloud') && (
                    <span style={{ marginLeft: '8px', color: selectedProvider.connected ? '#22c55e' : '#ef4444', fontSize: '11px' }}>
                      {selectedProvider.connected ? '● en ligne' : '○ hors ligne'}
                    </span>
                  )}
                </p>
              </div>
              <a href={selectedProvider.api_url} target="_blank" rel="noreferrer" style={{ color: '#4b5563', display: 'flex', alignItems: 'center' }}>
                <ExternalLink size={14} />
              </a>
            </div>

            {/* Models panel */}
            <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px' }}>
              <div style={{ fontSize: '10px', color: '#4b5563', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '7px' }}>
                Modèles disponibles
              </div>
              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                {selectedProvider.models.map(m => (
                  <span key={m} style={{ fontSize: '10px', background: '#1e293b', color: '#64748b', padding: '2px 8px', borderRadius: '6px', fontFamily: 'monospace' }}>{m}</span>
                ))}
              </div>
            </div>

            {/* Permission list */}
            {loadingPerms ? (
              <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px' }}>
                <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {/* Grouper par type */}
                {[
                  { group: 'CMS WordPress', types: ['cms'], icon: '🎸' },
                  { group: 'Hub Claudie (MCP VPS)', types: ['sub-hub'], icon: '🌐' },
                  { group: 'Proxy / Local VPS', types: ['proxy', 'http'], icon: '🤖' },
                  { group: 'Services Web (VPS)', types: ['webui'], icon: '🖥️' },
                  { group: 'Claude.ai Cloud', types: ['claude-ai'], icon: '☁️' },
                  { group: 'API Externes', types: ['external', 'database'], icon: '🔌' },
                ].map(({ group, types, icon }) => {
                  const groupPerms = permissions.filter(p => types.includes(p.connector_type))
                  if (groupPerms.length === 0) return null
                  const groupEnabled = groupPerms.filter(p => p.enabled).length
                  return (
                    <div key={group} style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '10px', color: '#4b5563', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 12px 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {icon} {group}
                        <span style={{ marginLeft: 'auto', color: groupEnabled > 0 ? '#22c55e' : '#374151', fontWeight: '600' }}>
                          {groupEnabled}/{groupPerms.length}
                        </span>
                      </div>
                      {groupPerms.map(perm => {
                        const connector = connectors.find(c => c.id === perm.connector_id)
                        return (
                          <div
                            key={perm.connector_id}
                            className="perm-row"
                            style={{
                              display: 'flex', alignItems: 'center', gap: '12px',
                              padding: '10px 14px', borderRadius: '8px', transition: 'background 0.15s',
                              background: perm.enabled ? '#0f1f0f' : 'transparent',
                              border: `1px solid ${perm.enabled ? '#166534' : '#1e293b'}`,
                              marginBottom: '3px',
                              opacity: !perm.connector_active ? 0.5 : 1,
                            }}
                          >
                            <span style={{ fontSize: '18px', flexShrink: 0 }}>{perm.connector_icon}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {perm.connector_name}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                                <TypeBadge type={perm.connector_type} />
                                {connector && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: connector.status === 'connected' ? '#22c55e' : '#4b5563' }}>
                                    <StatusDot status={connector.status} size={7} />
                                    {connector.status === 'connected' ? 'connecté' : connector.status === 'disconnected' ? 'hors ligne' : '…'}
                                  </span>
                                )}
                              </div>
                            </div>
                            {connector && (
                              <button
                                onClick={() => onViewTools(connector)}
                                title="Voir les outils"
                                style={{ background: 'none', border: 'none', color: '#4b5563', cursor: 'pointer', padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center' }}
                                onMouseEnter={e => e.currentTarget.style.color = '#a78bfa'}
                                onMouseLeave={e => e.currentTarget.style.color = '#4b5563'}
                              >
                                <Wrench size={13} />
                              </button>
                            )}
                            <MiniToggle
                              enabled={perm.enabled}
                              onChange={v => onTogglePerm(perm.connector_id, v)}
                            />
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ─── ConnectorsView ───────────────────────────────────────────────────────────

const OLLAMA_CLOUD_MCP_MODELS = [
  { id: 'gemma4:31b',          label: 'Gemma 4 31B',             note: 'modèle de test validé ✓' },
  { id: 'deepseek-v4-flash',   label: 'DeepSeek V4 Flash',       note: 'rapide, outil-loop' },
  { id: 'deepseek-v4-pro',     label: 'DeepSeek V4 Pro',         note: 'plus puissant' },
  { id: 'kimi-k2.7-code',      label: 'Kimi K2.7 Code',          note: 'codage spécialisé' },
  { id: 'kimi-k2.6',           label: 'Kimi K2.6',               note: '' },
  { id: 'kimi-k2.5',           label: 'Kimi K2.5',               note: '' },
  { id: 'qwen3.5:397b',        label: 'Qwen 3.5 397B',           note: 'ultra-large' },
  { id: 'glm-5.2-ollama',      label: 'GLM-5.2',                 note: '' },
  { id: 'glm-5.1-ollama',      label: 'GLM-5.1',                 note: '' },
  { id: 'mistral-large-3:675b',label: 'Mistral Large 3 675B',    note: '' },
  { id: 'nemotron-3-ultra',    label: 'Nemotron 3 Ultra',         note: 'NVIDIA' },
  { id: 'nemotron-3-super',    label: 'Nemotron 3 Super',         note: 'NVIDIA' },
  { id: 'nemotron-3-nano:30b', label: 'Nemotron 3 Nano 30B',     note: 'NVIDIA' },
  { id: 'minimax-m3',          label: 'MiniMax M3',               note: '' },
  { id: 'minimax-m2.7',        label: 'MiniMax M2.7',             note: '' },
  { id: 'minimax-m2.5',        label: 'MiniMax M2.5',             note: '' },
  { id: 'gpt-oss:120b',        label: 'GPT OSS 120B',             note: '' },
  { id: 'gpt-oss:20b',         label: 'GPT OSS 20B',              note: 'compact' },
]

function ConnectorsView({ connectors, onToggle, onDelete, onViewTools }: {
  connectors: MCPConnector[]
  onToggle: (c: MCPConnector) => void
  onDelete: (c: MCPConnector) => void
  onViewTools: (c: MCPConnector) => void
}) {
  const connected = connectors.filter(c => c.status === 'connected').length
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>

      {/* ── Section Ollama Cloud MCP ── */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <span style={{ fontSize: '16px' }}>🦙</span>
          <span style={{ fontSize: '12px', color: '#4ade80', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Ollama Cloud — 18 modèles avec accès MCP
          </span>
          <span style={{ fontSize: '10px', background: '#14532d', color: '#4ade80', padding: '1px 7px', borderRadius: '8px', fontWeight: '700' }}>
            MCP ACTIF
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '6px' }}>
          {OLLAMA_CLOUD_MCP_MODELS.map((m, i) => (
            <div key={m.id} style={{
              background: i === 0 ? '#0a1f0a' : '#111827',
              border: `1px solid ${i === 0 ? '#166534' : '#1e293b'}`,
              borderRadius: '7px', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '2px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {i === 0 && <Zap size={11} style={{ color: '#4ade80', flexShrink: 0 }} />}
                <span style={{ fontSize: '12px', color: i === 0 ? '#4ade80' : '#d1d5db', fontWeight: '600', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.label}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <span style={{ fontSize: '9px', color: '#4b5563', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.id}</span>
                {m.note && <span style={{ fontSize: '9px', color: '#374151', flexShrink: 0 }}>· {m.note}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Section MCP Servers ── */}
      <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '11px', color: '#4b5563', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          🔌 Serveurs MCP — {connected} connectés / {connectors.length}
        </span>
      </div>
      <div style={{ background: '#111827', borderRadius: '12px', border: '1px solid #1e293b', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#0f172a' }}>
              {['', 'Nom', 'Type', 'Statut', 'On/Off', 'Actions'].map((h, i) => (
                <th key={i} style={{ padding: '10px 14px', color: '#4b5563', fontSize: '10px', fontWeight: '700', textAlign: i === 0 ? 'center' : 'left', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #1e293b' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {connectors.map((c, idx) => (
              <tr key={c.id} className="connector-row" style={{ transition: 'background 0.15s', borderBottom: idx < connectors.length - 1 ? '1px solid #1e293b' : 'none' }}>
                <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: '20px', width: '44px' }}>{c.icon}</td>
                <td style={{ padding: '12px 14px' }}>
                  <div style={{ color: '#f3f4f6', fontWeight: '600', fontSize: '13px' }}>{c.name}</div>
                  {c.description && <div style={{ color: '#4b5563', fontSize: '11px', marginTop: '1px', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.description}</div>}
                </td>
                <td style={{ padding: '12px 14px' }}><TypeBadge type={c.type} /></td>
                <td style={{ padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <StatusDot status={c.status} />
                    <span style={{ fontSize: '12px', color: c.status === 'connected' ? '#22c55e' : c.status === 'disconnected' ? '#ef4444' : '#f59e0b' }}>
                      {c.status === 'connected' ? 'Connecté' : c.status === 'disconnected' ? 'Hors ligne' : 'Vérif…'}
                    </span>
                  </div>
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <MiniToggle enabled={c.enabled !== 0} onChange={() => onToggle(c)} />
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <ActionBtn color="#a78bfa" onClick={() => onViewTools(c)}><Wrench size={11} /> Outils</ActionBtn>
                    <ActionBtn color="#ef4444" onClick={() => onDelete(c)}><Trash2 size={11} /></ActionBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── ToolsModal ───────────────────────────────────────────────────────────────

function ToolsModal({ connector, tools, allTools, loading, search, onSearch, expandedTool, onToggleExpand, onClose }: {
  connector: MCPConnector; tools: any[]; allTools: any[]; loading: boolean
  search: string; onSearch: (s: string) => void; expandedTool: string | null
  onToggleExpand: (n: string) => void; onClose: () => void
}) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
      <div style={{ backgroundColor: '#111827', borderRadius: '16px', border: '1px solid #374151', width: '100%', maxWidth: '780px', height: '78vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '26px' }}>{connector.icon}</span>
            <div>
              <h2 style={{ color: '#f3f4f6', margin: 0, fontSize: '16px', fontWeight: '700' }}>{connector.name}</h2>
              <p style={{ color: '#6b7280', margin: 0, fontSize: '11px' }}>{loading ? 'Chargement…' : `${allTools.length} outils`}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        {!loading && allTools.length > 0 && (
          <div style={{ padding: '10px 22px', borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
            <input value={search} onChange={e => onSearch(e.target.value)} placeholder="Rechercher un outil…"
              style={{ width: '100%', padding: '7px 10px', background: '#0f172a', border: '1px solid #374151', borderRadius: '6px', color: '#f3f4f6', fontSize: '12px', outline: 'none', boxSizing: 'border-box' as const }} />
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 22px 20px' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6b7280' }}>
              <RefreshCw size={18} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} /> Récupération…
            </div>
          ) : tools.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#4b5563', padding: '40px' }}>{search ? 'Aucun résultat.' : 'Aucun outil disponible.'}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {tools.map(tool => {
                const isOpen = expandedTool === tool.name
                const params = tool.inputSchema?.properties ? Object.keys(tool.inputSchema.properties) : []
                const req = tool.inputSchema?.required || []
                return (
                  <div key={tool.name} style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: '7px', overflow: 'hidden' }}>
                    <button onClick={() => onToggleExpand(tool.name)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                      <Wrench size={12} style={{ color: '#a78bfa', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#e2e8f0', fontSize: '12px', fontWeight: '600', fontFamily: 'monospace' }}>{tool.name}</div>
                        {tool.description && !isOpen && <div style={{ color: '#6b7280', fontSize: '10px', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tool.description}</div>}
                      </div>
                      {params.length > 0 && <span style={{ fontSize: '10px', background: '#1e293b', color: '#64748b', padding: '1px 6px', borderRadius: '8px', flexShrink: 0 }}>{params.length}p</span>}
                      <span style={{ color: '#374151', flexShrink: 0 }}>{isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</span>
                    </button>
                    {isOpen && (
                      <div style={{ padding: '0 14px 12px', borderTop: '1px solid #1e293b' }}>
                        {tool.description && <p style={{ color: '#94a3b8', fontSize: '12px', margin: '10px 0 6px', lineHeight: '1.5' }}>{tool.description}</p>}
                        {params.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                            {params.map((p: string) => (
                              <span key={p} style={{ fontSize: '11px', fontFamily: 'monospace', background: req.includes(p) ? '#1e3a5f' : '#1e293b', color: req.includes(p) ? '#60a5fa' : '#94a3b8', border: `1px solid ${req.includes(p) ? '#1d4ed8' : '#374151'}`, padding: '1px 7px', borderRadius: '5px' }}>
                                {p}{req.includes(p) ? ' *' : ''}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── AddModal ─────────────────────────────────────────────────────────────────

function AddModal({ form, onChange, onSave, onClose, saving }: {
  form: NewConnectorForm; onChange: (f: NewConnectorForm) => void
  onSave: () => void; onClose: () => void; saving: boolean
}) {
  const set = (k: keyof NewConnectorForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => onChange({ ...form, [k]: e.target.value })
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
      <div style={{ backgroundColor: '#111827', borderRadius: '14px', border: '1px solid #374151', width: '100%', maxWidth: '460px', boxShadow: '0 25px 50px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ color: '#f3f4f6', margin: 0, fontSize: '16px', fontWeight: '700' }}>Ajouter un connecteur MCP</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {([['Nom *', 'name', 'Mon serveur MCP'], ['URL *', 'url', 'https://…/mcp'], ['Icône', 'icon', '🔌'], ['Token API', 'api_token', 'optionnel']] as const).map(([label, key, placeholder]) => (
            <div key={key}>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '11px', marginBottom: '4px' }}>{label}</label>
              <input value={form[key]} onChange={set(key)} placeholder={placeholder} style={{ width: '100%', padding: '7px 10px', background: '#0f172a', border: '1px solid #374151', borderRadius: '6px', color: '#f3f4f6', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const }} />
            </div>
          ))}
          <div>
            <label style={{ display: 'block', color: '#9ca3af', fontSize: '11px', marginBottom: '4px' }}>Type</label>
            <select value={form.type} onChange={set('type')} style={{ width: '100%', padding: '7px 10px', background: '#0f172a', border: '1px solid #374151', borderRadius: '6px', color: '#f3f4f6', fontSize: '13px' }}>
              {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
        <div style={{ padding: '14px 22px', borderTop: '1px solid #1e293b', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '7px 14px', background: 'transparent', border: '1px solid #374151', borderRadius: '7px', color: '#9ca3af', cursor: 'pointer', fontSize: '13px' }}>Annuler</button>
          <button onClick={onSave} disabled={saving || !form.name.trim() || !form.url.trim()} style={{ padding: '7px 16px', background: (!form.name.trim() || !form.url.trim()) ? '#374151' : 'linear-gradient(to right, #ec4899, #a855f7)', border: 'none', borderRadius: '7px', color: '#fff', fontWeight: '600', cursor: (!form.name.trim() || !form.url.trim()) ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
            {saving ? 'Ajout…' : '✓ Ajouter'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── ActionBtn ───────────────────────────────────────────────────────────────

function ActionBtn({ onClick, color, children, title }: { onClick: () => void; color: string; children: React.ReactNode; title?: string }) {
  const [h, setH] = useState(false)
  return (
    <button onClick={onClick} title={title} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ padding: '4px 8px', backgroundColor: h ? color : 'transparent', color: h ? '#fff' : color, border: `1px solid ${color}`, borderRadius: '5px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.15s' }}>
      {children}
    </button>
  )
}

// ─── ProviderActionBtn ────────────────────────────────────────────────────────

function ProviderActionBtn({ onClick, color, icon, label }: { onClick: () => void; color: string; icon: React.ReactNode; label: string }) {
  const [h, setH] = useState(false)
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{
      padding: '3px 7px', backgroundColor: h ? color + '33' : 'transparent', color: h ? color : '#6b7280',
      border: `1px solid ${h ? color + '66' : '#374151'}`, borderRadius: '4px', cursor: 'pointer',
      fontSize: '10px', display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.15s',
    }}>
      {icon} {label}
    </button>
  )
}
